import { FC, useEffect, useMemo, useRef, useState } from "react";
import InDialog from "components/InDialog/InDialog";
import translate from "src/lib/translate";
import styled from "@emotion/styled";
import { APP_BREAKPOINT } from "src/constants/layout";
import Translate from "components/Translate/Translate";
import { useBloc } from "src/state/state";
import DevicesCubit from "src/state/DevicesCubit/DevicesCubit";
import { DeviceItem } from "@9amhealth/openapi";
import { stepOnYourScalePopupConstant } from "./stepOnYourScalePopup.constant";
import SubscriptionCubit from "src/state/SubscriptionCubit/SubscriptionCubit";

const { stepOnYourScaleImageUrlVar, getScaleImage, scaleImageSkuSet } =
  stepOnYourScalePopupConstant;

const StepOnYourScaleWrapper = styled.div`
  display: flex;
  justify-self: center;
  width: 394px;
  height: 100%;
  flex-flow: column nowrap;
  align-items: center;
  text-align: center;

  padding-top: 80px;
  @media screen and (max-width: ${APP_BREAKPOINT}px) {
    width: 327px;
    padding-top: 40px;
  }
`;

const StepOnYourScaleImage = styled.div`
  ${stepOnYourScaleImageUrlVar}: "";

  width: 211px;
  height: 207px;

  background-image: var(${stepOnYourScaleImageUrlVar});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center center;

  filter: drop-shadow(0 0 0 rgba(90, 68, 0, 0.32))
    drop-shadow(-6px 4px 15px rgba(90, 68, 0, 0.32))
    drop-shadow(-23px 15px 28px rgba(90, 68, 0, 0.27))
    drop-shadow(-53px 33px 37px rgba(90, 68, 0, 0.16));
`;

const StepOnYourScaleDescription = styled.div`
  margin-top: 45px;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%; /* 22.4px */
  letter-spacing: -0.32px;
`;

const StepOnYourScaleAutoSpace = styled.div`
  flex: 1 1 auto;
`;

const StepOnYourScaleWarning = styled.div`
  border-radius: var(--border-radius);
  background: var(--color-cream-dark, #f2efe7);
  padding: 8px 12px;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
`;

export const StepOnYourScalePopup: FC<{
  returnUrl: string;
}> = () => {
  const [{ devices }, { getUserDevices }] = useBloc(DevicesCubit);
  const [initialized, setInitialized] = useState<boolean>(false);
  const scales = useMemo<DeviceItem[]>(
    () =>
      devices.filter((device) => device.type === DeviceItem.type.WEIGHT_SCALE),
    [devices]
  );
  const [skuInitialized, setSkuInitialized] = useState<boolean>(false);
  const [{ allSubscriptions }, { loadAllSubscriptions }] =
    useBloc(SubscriptionCubit);
  const skuDevices = useMemo(
    () =>
      allSubscriptions
        ?.map(({ purchaseItems }) =>
          purchaseItems
            .filter(({ sku }) => scaleImageSkuSet.has(sku))
            .map(({ sku }) => sku)
        )
        .filter((deviceSkus) => Boolean(deviceSkus.length))
        .flat(),
    [allSubscriptions]
  );

  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void getUserDevices();
    setInitialized(true);
  }, [getUserDevices]);

  useEffect(() => {
    void loadAllSubscriptions({ cachedSubscriptionsAcceptable: true });
    setSkuInitialized(true);
  });

  useEffect(() => {
    if (!imageRef.current) {
      return;
    }
    if (!scales.length && !initialized) {
      return;
    }
    if (!skuInitialized && !skuDevices) {
      return;
    }

    imageRef.current.style.setProperty(
      stepOnYourScaleImageUrlVar,
      getScaleImage({ scales, skuDevices })
    );
  }, [
    scales,
    initialized,
    skuInitialized,
    skuDevices,
    getScaleImage,
    stepOnYourScaleImageUrlVar
  ]);

  return (
    <InDialog
      popup
      title={translate("task.stepOnYourScale.popup.title")}
      width="min(calc(100% - 4rem), 120rem)"
      height="min(calc(100% - 4rem), 70rem)"
    >
      <nine-spacer s="md"></nine-spacer>
      <StepOnYourScaleWrapper>
        <StepOnYourScaleImage ref={imageRef} />
        <StepOnYourScaleDescription>
          <Translate msg={"task.stepOnYourScale.popup.description"} />
        </StepOnYourScaleDescription>
        <StepOnYourScaleAutoSpace />
        <StepOnYourScaleWarning>
          <Translate msg={"task.stepOnYourScale.popup.warning"} />
        </StepOnYourScaleWarning>
      </StepOnYourScaleWrapper>
    </InDialog>
  );
};

export default StepOnYourScalePopup;
